import { toValue } from 'vue'

import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { BaseService, type SSRNuxtAppLike } from '@common/http-layer/service/base.service'
import { useLocation } from '@common/location-layer/composables/useLocation'

import { mapPinnedGroupApiItemToIGroup } from '../utils/map-pinned-group-api-item'

import {
  type IGroup,
  type IGroupBranding,
  type IGroupPinRecord,
  type IGroupSettings,
  type IPinnedGroupApiItem,
  type NewsletterConfigToPatchAPI,
  type UpdateGroupTabsConfigurationItem,
} from '../types/types'
import type { SyncSubscriptionResult } from '../types/group-tiers'

import type {
  GroupMemberRole,
  IMembershipCustomFieldGroup,
  IMembershipQuestion,
  IMembershipQuestions,
  IGroupModerationSettings,
} from '../types/types'

export interface CustomFieldRefDTO {
  id: string
  key: string
}

export interface FormQuestionDTO {
  answerType: IMembershipQuestion['answerType']
  question: string
  answerOptions?: string[]
  customField?: CustomFieldRefDTO
}

export interface MembershipQuestionsDTO extends Omit<IMembershipQuestions, 'questions'> {
  questions: FormQuestionDTO[]
}

export interface CreateGroupInput {
  name: string
  description?: string
  about?: string
  isPublic?: boolean
  media?: any[]
  branding?: IGroupBranding
  settings?: IGroupSettings
}

export interface GroupResponse {
  groupDetail: IGroup
  membershipQuestions?: IMembershipQuestions
  // user?: any[]
  owner?: any
}

export interface PaginationParams {
  limit?: number
  skip?: number
  offset?: number
  search?: string
}

export interface GroupMemberRoleFilters {
  memberRole?: GroupMemberRole[]
}

/**
 * Groups service - handles all group-related API calls
 * Base URL: COMMUNITIES_SERVER_URL
 * Base Path: /communities/{locationId}/groups
 */
export class GroupsService extends BaseService {
  /**
   * @param ssrNuxtApp Optional pre-resolved nuxtApp for SSR contexts where
   *   `useNuxtApp()` is not reachable (e.g., nitro handlers called outside
   *   composable setup). Forwarded to BaseService so the 401 refresh chain and
   *   auth-token attachment work end-to-end on server-side data fetches.
   */
  constructor(ssrNuxtApp?: SSRNuxtAppLike) {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '', // Static part of the path
      serviceName: 'GroupsService', // Explicit service name for logging
      nuxtApp: ssrNuxtApp,
    })
  }

  /**
   * Build location-aware endpoint
   */
  private endpoint(path: string = '', locationId?: string): string {
    if (locationId) {
      return `/${locationId}/groups${path}`
    }
    const { store: state } = useLocation()

    if (!state.locationId) {
      console.error('[GroupsService.endpoint] Location ID is missing from state:', state)
      throw new Error('Location ID is required')
    }

    const fullEndpoint = `/${state.locationId}/groups${path}`

    return fullEndpoint
  }

  /**
   * Get discoverable groups (public groups for discovery/explore)
   * GET /communities/{locationId}/groups/not-joined?skip=0&limit=10
   */
  async getDiscoveryGroups(locationId?: string, params?: { skip?: number; limit?: number }): Promise<IGroup[]> {
    try {
      const endpoint = this.endpoint('/not-joined', locationId)
      const { data } = await this.get<IGroup[]>(endpoint, { params })
      return data || []
    } catch (error) {
      console.error('[GroupsService.getDiscoveryGroups] Error fetching discovery groups:', error)
      throw error
    }
  }

  /**
   * Get all groups
   */
  async getGroups(params?: PaginationParams): Promise<IGroup[]> {
    try {
      // Support both 'skip' and 'offset' parameters
      const queryParams = params
        ? {
            ...params,
          }
        : undefined

      const endpoint = this.endpoint()
      const { data } = await this.get<IGroup[]>(endpoint, { params: queryParams })
      return data || []
    } catch (error) {
      console.error('[GroupsService.getGroups] Error fetching groups:', error)
      throw error
    }
  }

  /**
   * Create a new group
   */
  // async createGroup(input: CreateGroupInput): Promise<IGroup> {
  //   const { data } = await this.post(this.endpoint(), input)
  //   return data
  // }

  /**
   * Update a group
   */
  async updateGroup(groupId: string, updates: Partial<CreateGroupInput>, locationId: string): Promise<IGroup> {
    const { data } = await this.put<IGroup>(this.endpoint(`/${groupId}`, locationId), updates, {
      groupId,
      locationId,
    })
    return data
  }

  async updateGroupTabsConfiguration(
    groupId: string,
    locationId: string,
    tabsConfiguration: UpdateGroupTabsConfigurationItem[]
  ): Promise<IGroup> {
    const { data } = await this.put<IGroup>(
      this.endpoint(`/${groupId}/tabs-configuration`, locationId),
      { tabsConfiguration },
      {
        groupId,
        locationId,
      }
    )
    return data
  }

  /**
   * Leave a group (remove current user's membership)
   * DELETE /communities/{locationId}/groups/{groupId}/user-group
   */
  async leaveGroup(groupId: string, locationId?: string): Promise<void> {
    const { store: state } = useLocation()
    const lId = locationId ?? state.locationId
    if (!lId) {
      console.error('[GroupsService.leaveGroup] Location ID is missing from state:', state)
      throw new Error('Location ID is required')
    }
    await this.delete(this.endpoint(`/${groupId}/user-group`, lId), {
      groupId,
      locationId: lId,
    })
  }

  /**
   * Get group by slug
   */
  async getGroupBySlug(slug: string): Promise<GroupResponse> {
    try {
      const endpoint = this.endpoint(`/slug/${slug}`)

      const { data } = await this.get<GroupResponse>(endpoint)

      return data
    } catch (error) {
      console.error('[GroupsService.getGroupBySlug] Error fetching group by slug:', error)
      throw error
    }
  }

  /**
   * Get group by ID
   * @param locationId Location the group belongs to. Required when the group is not from the
   *   active location (e.g. content rendered in profile contributions or search results),
   *   otherwise the endpoint falls back to the active location and the lookup 404s.
   */
  async getGroupById(id: string, locationId?: string): Promise<GroupResponse> {
    const { data } = await this.get<GroupResponse>(this.endpoint(`/${id}`, locationId))
    return data
  }

  /**
   * Get groups by location (alias for getGroups)
   */
  async getGroupByLocation(query?: PaginationParams): Promise<IGroup[]> {
    return this.getGroups(query)
  }

  /**
   * Get pinned groups
   */
  async getPinnedGroups(): Promise<IGroup[]> {
    try {
      const { data } = await this.get<IPinnedGroupApiItem[]>(this.endpoint('/pinned'))
      return (data ?? []).map(mapPinnedGroupApiItemToIGroup)
    } catch (error) {
      console.error('Failed to fetch pinned groups:', error)
      return []
    }
  }

  /**
   * Pin a group
   */
  async pinGroup(groupId: string): Promise<IGroupPinRecord> {
    const { data } = await this.post<IGroupPinRecord>(this.endpoint(`/${groupId}/pin`))
    return data
  }

  /**
   * Unpin a group
   */
  async unpinGroup(groupId: string): Promise<void> {
    await this.post(this.endpoint(`/${groupId}/unpin`))
  }

  /**
   * Sync pricing for a group
   */
  async syncPricing(groupId: string): Promise<any> {
    const { data } = await this.put(this.endpoint(`/${groupId}/sync-pricing`))
    return data
  }

  /**
   * Sync legacy PAID groups onto a Default subscription tier (idempotent).
   * Soft-skips FREE / incomplete groups on the backend.
   * POST /communities/{locationId}/groups/{groupId}/sync-subscription
   * @param groupId - The group ID
   * @param locationId - The group's own location (not the portal location — required for GoKollab)
   */
  async syncSubscription(groupId: string, locationId: string): Promise<SyncSubscriptionResult> {
    const { data } = await this.post<SyncSubscriptionResult>(this.endpoint(`/${groupId}/sync-subscription`, locationId), undefined, {
      groupId,
      locationId,
    })
    return data
  }

  async fetchMembershipQuestionsForm(groupId: string, locationId: string, formId: string): Promise<MembershipQuestionsDTO> {
    const { data } = await this.get<MembershipQuestionsDTO>(this.endpoint(`/${groupId}/forms/${formId}`, locationId))
    return data
  }

  async fetchMembershipCustomFields(groupId: string, locationId: string): Promise<IMembershipCustomFieldGroup[]> {
    const { data } = await this.get<IMembershipCustomFieldGroup[]>(this.endpoint(`/${groupId}/forms/custom-fields`, locationId), {
      groupId,
      locationId,
    })
    return data
  }

  async upsertMembershipQuestionsForm(groupId: string, locationId: string, questions: FormQuestionDTO[]): Promise<MembershipQuestionsDTO> {
    const { data } = await this.post<MembershipQuestionsDTO>(
      this.endpoint(`/${groupId}/forms`, locationId),
      {
        locationId,
        groupId,
        questions,
      },
      { groupId, locationId }
    )
    return data
  }

  async updateNewsletterConfig(groupId: string, locationId: string, config: NewsletterConfigToPatchAPI): Promise<IGroup> {
    const { data } = await this.patch<IGroup>(this.endpoint(`/${groupId}/newsletter-config`, locationId), config, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Get user's groups/communities
   * GET /communities/{locationId}/groups
   * Shared endpoint for both GoKollab and Client Portal.
   * @param locationId - Optional location ID (will use from state if not provided)
   * @param params - Query parameters for filtering groups
   * @returns Promise<any>
   * @todo Confirm with backend whether GoKollab and Client Portal should use separate endpoints
   */
  async getUserGroups(
    locationId?: string,
    params?: {
      contactId?: string
      sort?: string
      sortKey?: string
      limit?: number
      status?: string
    }
  ): Promise<any> {
    const { store: state } = useLocation()
    const { isGokollab } = useGlobal()

    const lId = locationId || state.locationId
    if (!lId) {
      console.error('[GroupsService.getUserGroups] Location ID is missing from state:', state)
      throw new Error('Location ID is required')
    }

    const endpoint = isGokollab ? `/${lId}/groups` : `/${lId}/users/groups`

    const { data } = await this.get(endpoint, {
      params,
      locationId: lId,
    })
    return data
  }

  /**
   * Toggle chat preference for a group
   * POST /communities/{locationId}/groups/{groupId}/update-chat-preference
   * Shared endpoint for both GoKollab and Client Portal.
   * Request body: { isChatDisabled: boolean } (inverse of preferences.chatEnabled)
   * @param groupId - The group ID
   * @param preferences - Chat preference settings ({ chatEnabled: boolean })
   * @param locationId - Optional location ID (will use from state if not provided)
   * @returns Promise<any>
   * @todo Confirm with backend whether GoKollab and Client Portal should use separate endpoints
   */
  async toggleChatPreference(groupId: string, preferences: any, locationId?: string): Promise<any> {
    const { store: state } = useLocation()

    const lId = locationId || state.locationId

    if (!lId) {
      console.error('Location ID is missing from state:', state)
      throw new Error('Location ID is required')
    }

    const endpoint = `/${lId}/groups/${groupId}/update-chat-preference`

    const requestBody = { isChatDisabled: !preferences.chatEnabled }

    const { data } = await this.post(endpoint, requestBody, {
      groupId,
      locationId: lId,
    })
    return data
  }
}
